import React, { useState } from "react";
import { Field } from "react-final-form";
import { Alert } from "@material-ui/lab";
import { useQuery } from "@tanstack/react-query";
import { FieldArray } from "react-final-form-arrays";
import MuiCheckbox from "@material-ui/core/Checkbox";
import Select from "../../Shared/Select";
import { Trans } from "../../utils/i18n";
import { axiosClient } from "../../utils/axiosClient";
import classNames from "classnames";

const SelectReturnItems = React.memo(
    function ({ isViewMode }) {
        const [error, setError] = useState(false);
        const { data: returnReasons } = useQuery({
            queryKey: ["returnReasons"],
            queryFn: () => axiosClient.get(route("order.return.reasons")),
            onError: (error) => {
                setError(
                    error?.response?.data?.message ?? (
                        <Trans i18nKey="returnOrder.errors.returnReasonsCouldNotBeFetched">
                            Return reasons could not be fetched
                        </Trans>
                    )
                );
            },
        });

        return (
            <div className="space-y-8">
                <Field name="parent_order_number">
                    {({ input }) => (
                        <div>
                            <div className="field-wrapper">
                                <input {...input} type="hidden" />
                            </div>
                        </div>
                    )}
                </Field>

                <FieldArray name="products">
                    {({ fields }) => {
                        return (
                            <ul className="border border-gray-200 rounded-md overflow-hidden divide-y divide-gray-200 m-0 p-0">
                                {fields.map((name, index) => (
                                    <li
                                        key={name}
                                        className={classNames({
                                            hidden:
                                                !fields.value[index].isReturn &&
                                                isViewMode,
                                        })}
                                    >
                                        <label
                                            className={classNames(
                                                "grid grid-cols-2 gap-4 sm:grid-cols-4 p-2 hover:bg-gray-100 transition-colors",
                                                {
                                                    "cursor-pointer":
                                                        !isViewMode,
                                                }
                                            )}
                                            htmlFor={`${name}.isReturn`}
                                        >
                                            <div className="col-span-2 flex items-center gap-2">
                                                <Field
                                                    id={`${name}.isReturn`}
                                                    name={`${name}.isReturn`}
                                                    type="checkbox"
                                                >
                                                    {(props) => (
                                                        <MuiCheckbox
                                                            {...props.input}
                                                            disabled={
                                                                isViewMode
                                                            }
                                                            value={
                                                                props.input
                                                                    .value ??
                                                                false
                                                            }
                                                            id={`${name}.isReturn`}
                                                        />
                                                    )}
                                                </Field>

                                                <div>
                                                    {fields.value[index].title}
                                                </div>
                                            </div>

                                            <Field
                                                name={`${name}.amount`}
                                                id={`${name}.amount`}
                                                type="select"
                                                component={Select}
                                                size="small"
                                                disabled={isViewMode}
                                                field={{
                                                    type: "select",
                                                    label: (
                                                        <Trans i18nKey="Quantity">
                                                            Quantity
                                                        </Trans>
                                                    ),
                                                    options: Array.from(
                                                        Array(
                                                            fields.value[index]
                                                                .maxQuantity
                                                        ).keys()
                                                    ).map((i) => ({
                                                        value: i + 1,
                                                        label: i + 1,
                                                    })),
                                                    disableUnselected: false,
                                                    style: {
                                                        minWidth: "200px",
                                                    },
                                                }}
                                            />

                                            <Field
                                                name={`${name}.problem_1`}
                                                id={`${name}.problem_1`}
                                                type="select"
                                                component={Select}
                                                disabled={isViewMode}
                                                field={{
                                                    type: "select",
                                                    label: (
                                                        <Trans i18nKey="common.reason">
                                                            Reason
                                                        </Trans>
                                                    ),
                                                    options:
                                                        returnReasons?.data?.map(
                                                            (reason) => ({
                                                                value: reason.id,
                                                                label: reason.name,
                                                            })
                                                        ) ?? [],
                                                    disableUnselected: false,
                                                }}
                                            />
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        );
                    }}
                </FieldArray>

                {error && <Alert severity="error">{error}</Alert>}
            </div>
        );
    },
    function areEqual(prevProps, nextProps) {
        return JSON.stringify(prevProps) === JSON.stringify(nextProps);
    }
);

export default SelectReturnItems;
